import React, { useEffect, useState } from "react";
import { Save, RefreshCw, Power, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export const AdminSettingsPanel = ({ token }) => {
    const headers = { "X-Admin-Token": token };
    const [s, setS] = useState(null);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/settings", { headers });
            setS(data);
        } catch {
            toast.error("Failed to load settings");
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    const update = (k, v) => setS((p) => ({ ...p, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                dispatch_enabled: s.dispatch_enabled,
                dispatch_cutoff_hour: Number(s.dispatch_cutoff_hour),
                dispatch_cutoff_minute: Number(s.dispatch_cutoff_minute),
                dispatch_timezone: s.dispatch_timezone,
                dispatch_weekend_enabled: s.dispatch_weekend_enabled,
                dispatch_text_before: s.dispatch_text_before,
                dispatch_text_after: s.dispatch_text_after,
            };
            await api.patch("/admin/settings", payload, { headers });
            toast.success("Settings saved");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
        setSaving(false);
    };

    if (!s) return <div className="text-[var(--text-secondary)] py-12 text-center">Loading…</div>;

    return (
        <div className="space-y-5" data-testid="admin-settings-panel">
            <div className="flex items-center justify-between">
                <h3 className="font-display text-xl font-bold text-white inline-flex items-center gap-2">
                    <Clock size={18} className="text-[var(--gold)]" /> Dispatch Countdown
                </h3>
                <button onClick={load} className="btn-neon" data-testid="settings-refresh">
                    <RefreshCw size={14} /> Reload
                </button>
            </div>

            <label className="flex items-center justify-between p-4 rounded-xl glass border border-white/5 cursor-pointer">
                <div>
                    <div className="text-sm text-white font-medium">Show countdown timer on site</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-0.5">Product pages + cart</div>
                </div>
                <input
                    type="checkbox"
                    checked={!!s.dispatch_enabled}
                    onChange={(e) => update("dispatch_enabled", e.target.checked)}
                    className="w-5 h-5 accent-[var(--gold)]"
                    data-testid="settings-dispatch-enabled"
                />
            </label>

            <div className="grid md:grid-cols-2 gap-3">
                <div>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Cutoff hour (0–23)</label>
                    <input
                        type="number" min="0" max="23"
                        value={s.dispatch_cutoff_hour}
                        onChange={(e) => update("dispatch_cutoff_hour", e.target.value)}
                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                        data-testid="settings-cutoff-hour"
                    />
                </div>
                <div>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Cutoff minute (0–59)</label>
                    <input
                        type="number" min="0" max="59"
                        value={s.dispatch_cutoff_minute}
                        onChange={(e) => update("dispatch_cutoff_minute", e.target.value)}
                        className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                        data-testid="settings-cutoff-minute"
                    />
                </div>
            </div>

            <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Timezone</label>
                <input
                    type="text"
                    value={s.dispatch_timezone}
                    onChange={(e) => update("dispatch_timezone", e.target.value)}
                    className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                    data-testid="settings-timezone"
                />
                <p className="text-[10px] text-[var(--text-muted)] mt-1.5">Default: Australia/Sydney</p>
            </div>

            <label className="flex items-center justify-between p-4 rounded-xl glass border border-white/5 cursor-pointer">
                <div>
                    <div className="text-sm text-white font-medium">Dispatch on weekends</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-0.5">If off, countdown skips Sat/Sun</div>
                </div>
                <input
                    type="checkbox"
                    checked={!!s.dispatch_weekend_enabled}
                    onChange={(e) => update("dispatch_weekend_enabled", e.target.checked)}
                    className="w-5 h-5 accent-[var(--gold)]"
                    data-testid="settings-weekend-enabled"
                />
            </label>

            <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Text before cutoff</label>
                <input
                    type="text"
                    value={s.dispatch_text_before}
                    onChange={(e) => update("dispatch_text_before", e.target.value)}
                    className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                    data-testid="settings-text-before"
                />
                <p className="text-[10px] text-[var(--text-muted)] mt-1.5">Use <code>{`{cutoff}`}</code> as a placeholder for the cutoff time.</p>
            </div>

            <div>
                <label className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Text after cutoff</label>
                <input
                    type="text"
                    value={s.dispatch_text_after}
                    onChange={(e) => update("dispatch_text_after", e.target.value)}
                    className="w-full px-4 py-2.5 bg-black/40 border border-white/10 rounded-full text-sm text-white focus:outline-none focus:border-[var(--gold)]"
                    data-testid="settings-text-after"
                />
            </div>

            <button onClick={save} disabled={saving} className="btn-solid" data-testid="settings-save">
                <Save size={14} /> {saving ? "Saving…" : "Save changes"}
            </button>

            <p className="text-[10px] text-[var(--text-muted)] mt-3">
                <strong>Public holidays</strong> are managed in <code>/app/backend/business_hours.py</code> (AU_HOLIDAYS list).
                Add a new ISO date there and redeploy to skip the timer on that day.
            </p>
        </div>
    );
};
